import React, { useState } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import Modal from './Modal';
import { adminColors } from '../../styles/adminStyles';

const InfoButtonStyled = styled.button`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid ${adminColors.borderLight};
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${adminColors.primary};
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: bold;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-color: ${adminColors.primary};
    transform: scale(1.1);
  }
`;

const InfoContent = styled.div`
  font-family: 'Courier New', monospace;
  color: ${adminColors.text};
`;

const InfoSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoTitle = styled.h3`
  font-size: 14px;
  color: ${adminColors.primary};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${adminColors.borderLight};
`;

const InfoText = styled.p`
  font-size: 13px;
  line-height: 1.7;
  color: ${adminColors.textMuted};
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FieldItem = styled.li`
  padding: 10px 12px;
  margin-bottom: 8px;
  background: rgba(0, 20, 40, 0.5);
  border: 1px solid ${adminColors.borderMuted};
  border-radius: 6px;
  font-size: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldName = styled.span`
  color: ${adminColors.primary};
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
`;

const FieldDescription = styled.span`
  color: ${adminColors.textMuted};
  line-height: 1.5;
`;

const Highlight = styled.span`
  color: ${adminColors.warning};
  font-weight: 600;
`;

export interface InfoField {
  name: string;
  description: string;
}

export interface InfoSectionData {
  title: string;
  description?: string;
  fields?: InfoField[];
}

interface InfoButtonProps {
  title: string;
  sections: InfoSectionData[];
}

const InfoButton: React.FC<InfoButtonProps> = ({ title, sections }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <InfoButtonStyled onClick={() => setIsOpen(true)} title="Show help">
        ?
      </InfoButtonStyled>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title={title}>
        <InfoContent>
          {sections.map((section, index) => (
            <InfoSection key={index}>
              <InfoTitle>{section.title}</InfoTitle>
              {section.description && (
                <InfoText dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.description || '') }} />
              )}
              {section.fields && section.fields.length > 0 && (
                <FieldList>
                  {section.fields.map((field, fieldIndex) => (
                    <FieldItem key={fieldIndex}>
                      <FieldName>{field.name}</FieldName>
                      <FieldDescription dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.description || '') }} />
                    </FieldItem>
                  ))}
                </FieldList>
              )}
            </InfoSection>
          ))}
        </InfoContent>
      </Modal>
    </>
  );
};

export default InfoButton;
export { Highlight };
